/* eslint-disable prettier/prettier */
import { Box, styled, Typography, Paper } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { dimensions } from '../../../../styles/dimensions';
import { theme } from '../../../../styles/theme';
import { Processor, ProcessorObject } from '../../../../models/processor';
import { CustomTable } from '../../../../components/CustomTable';

interface ProcessorInformationProps {
  processor: Processor;
}

export const ProcessorInformation: React.FunctionComponent<ProcessorInformationProps> = ({ processor }) => {
  const { t } = useTranslation();

  const processorInfoDetails = [
    { label: t('processor.ip'), value: processor.proIp },
    { label: t('processor.port'), value: processor.proPort.toString() },
    { label: t('processor.location'), value: processor.locationName || '-' },
    { label: t('processor.use_ssl'), value: processor.proUseSSL ? t('yes') : t('no') },
    { label: t('processor.use_alarm'), value: processor.proUseAlarm ? t('yes') : t('no') },
    { label: t('processor.broker'), value: processor.brokerName || '-' }
  ];

  // Get readers from processor
  const readers = processor.readers || [];

  // Zorg ervoor dat we altijd precies 4 rijen hebben
  const fixedReadersData = Array(4).fill(null).map((_, index) => {
    if (index < readers.length) {
      // Gebruik bestaande reader data
      const reader = readers[index];
      return {
        objectName: reader.objectName,
        objectOpenTime: reader.objectOpenTime || '-',
        objectChar: reader.objectChar,
        objectAntiPassback: reader.objectAntiPassback,
        objectMFA: reader.objectMFA,
        objectRouting: reader.objectRouting,
        objectMFAReader: reader.objectMFAReader,
        objectLeaderZero: reader.objectLeaderZero
      };
    } else {
      // Vul aan met lege rijen
      return {
        objectName: '-',
        objectOpenTime: '-',
        objectChar: '-',
        objectAntiPassback: '-',
        objectMFA: '-',
        objectRouting: '-',
        objectMFAReader: '-',
        objectLeaderZero: '-'
      };
    }
  });

  // Define table columns for readers
  const readerColumns = [
    {
      dataKey: 'objectName',
      fieldName: t('processor.object.name')
    },
    {
      dataKey: 'objectOpenTime',
      fieldName: t('processor.object.open_time')
    },
    {
      dataKey: 'objectChar',
      fieldName: t('processor.object.char')
    },
    {
      dataKey: 'objectAntiPassback',
      fieldName: t('processor.object.anti_passback')
    },
    {
      dataKey: 'objectMFA',
      fieldName: t('processor.object.mfa')
    },
    {
      dataKey: 'objectRouting',
      fieldName: t('processor.object.routing')
    },
    {
      dataKey: 'objectMFAReader',
      fieldName: t('processor.object.mfa_reader')
    },
    {
      dataKey: 'objectLeaderZero',
      fieldName: t('processor.object.leader_zero')
    }
  ];

  return (
    <ContentWrapper>
      <InfoSection>
        {processorInfoDetails.map((item, index) => (
          <InfoLineWrapper key={index}>
            <Label variant="body1">{item.label}</Label>
            <Typography variant="body1" color="grey.100">
              {item.value}
            </Typography>
          </InfoLineWrapper>
        ))}
      </InfoSection>

      <ObjectsSection>
        <Title variant="body1">{t('processor.readers')}</Title>
        <FixedHeightTableWrapper>
          <CustomTable 
            columns={readerColumns} 
            dataList={fixedReadersData} 
            cellMaxWidth="130px"
          />
        </FixedHeightTableWrapper>
      </ObjectsSection>
    </ContentWrapper>
  );
};

const { grey } = theme.palette;
const { base, baseXS, baseLG, baseMD, baseSM } = dimensions;

const Title = styled(Typography)`
  color: ${grey[100]};
  margin: ${base} 0;
  font-weight: 500;
`;

const ContentWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  width: calc(100% - 48px);
  padding: 0 ${baseLG};
  height: calc(100% - 100px);
  overflow-y: auto;
`;

const InfoSection = styled(Box)`
  display: flex;
  flex-direction: column;
  row-gap: ${baseSM};
  padding-top: ${baseMD};
  padding-bottom: ${baseMD};
  border-bottom: 1px solid ${grey[400]};
`;

const ObjectsSection = styled(Box)`
  display: flex;
  flex-direction: column;
  padding-top: ${baseMD};
  flex-grow: 1;
`;

const InfoLineWrapper = styled(Box)`
  border-bottom: 1px solid ${grey[400]};
  display: flex;
  flex-direction: row;
  padding-bottom: ${baseSM};
  margin-bottom: ${baseXS};
`;

const Label = styled(Typography)`
  min-width: 200px;
  color: ${grey[200]};
`;

const FixedHeightTableWrapper = styled(Box)`
  height: 200px; /* Hoogte voor precies 4 rijen */
  overflow: hidden; /* Verberg eventuele overflow */
  margin-bottom: ${baseMD};
  
  /* Zorg ervoor dat elke rij een vaste hoogte heeft */
  & .MuiTableRow-root {
    height: 50px; /* 200px / 4 rijen = 50px per rij */
  }
`;

const AdditionalInfoSection = styled(Box)`
  padding: ${baseMD};
  background-color: ${grey[600]};
  border-radius: ${baseXS};
  min-height: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
`;
